/*
 * Keeps the legitimate acquisition source across the external MitID age-check
 * redirect. This is an analytics/reporting aid only; it must never be used for
 * access control or for deciding whether a customer is old enough to buy.
 *
 * The age-check provider is an external redirect. Without this small bridge,
 * the first page after the check can be reported as a referral from MitID.
 * Attribution is only persisted after the matching Shopify consent purpose is
 * granted: analytics for organic/referral traffic and marketing for campaign
 * parameters/click IDs. If consent is absent, the customer is still returned
 * to the original page, but no tracking parameters are restored.
 */
(function () {
  'use strict';

  var CALLBACK_PATH = '/a/eid/complete';
  var STORAGE_KEY = 'devinos_attribution_repair_v1';
  var MAX_AGE_MS = 2 * 60 * 60 * 1000;
  var TRACKING_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
    'gclid', 'fbclid', 'msclkid', 'ttclid', 'sclid'
  ];
  var CLICK_ID_KEYS = ['gclid', 'fbclid', 'msclkid', 'ttclid', 'sclid'];
  var MARKETING_MEDIUMS = /^(cpc|ppc|paid|paid_social|social|social_paid|display|affiliate|influencer|email|sms|newsletter|retargeting|remarketing|video|shopping|cpm|cpv|cpp)$/i;
  var MITID_HOST = /(^|\.)(mitid|e-id|eid|age[-_]?check|alders[-_]?kontrol)(\.[a-z0-9-]+)+$/i;

  function storage() {
    try {
      return window.sessionStorage;
    } catch (error) {
      try { return window.localStorage; } catch (ignored) { return null; }
    }
  }

  function consentState() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return { analytics: false, marketing: false };
    try {
      return {
        analytics: typeof privacy.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed(),
        marketing: typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed()
      };
    } catch (error) {
      return { analytics: false, marketing: false };
    }
  }

  function isMarketingTracking(tracking, explicitCampaign) {
    return CLICK_ID_KEYS.some(function (key) { return Boolean(tracking[key]); }) ||
      Boolean(explicitCampaign) ||
      MARKETING_MEDIUMS.test(String(tracking.utm_medium || ''));
  }

  function trackingAllowed(tracking, explicitCampaign) {
    var state = consentState();
    return isMarketingTracking(tracking, explicitCampaign) ? state.marketing : state.analytics;
  }

  function sameSite(url) {
    try {
      var host = new URL(url).hostname.toLowerCase();
      var current = window.location.hostname.toLowerCase();
      return host === current || host === 'www.' + current || host.endsWith('.devinos.dk');
    } catch (error) {
      return false;
    }
  }

  function classifyReferrer(referrer) {
    if (!referrer || sameSite(referrer)) return null;
    try {
      var host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
      if (MITID_HOST.test(host)) return null;
      if (/google\./.test(host)) return { source: 'google', medium: 'organic' };
      if (/bing\./.test(host)) return { source: 'bing', medium: 'organic' };
      if (/duckduckgo\./.test(host)) return { source: 'duckduckgo', medium: 'organic' };
      if (/(^|\.)facebook\.com$|(^|\.)instagram\.com$|(^|\.)l\.facebook\.com$/.test(host)) {
        return { source: host.indexOf('instagram') >= 0 ? 'instagram' : 'facebook', medium: 'social' };
      }
      if (/(^|\.)tiktok\.com$/.test(host)) return { source: 'tiktok', medium: 'social' };
      return { source: host, medium: 'referral' };
    } catch (error) {
      return null;
    }
  }

  function read() {
    var store = storage();
    if (!store) return null;
    try {
      var value = JSON.parse(store.getItem(STORAGE_KEY) || 'null');
      return value && Date.now() - value.savedAt < MAX_AGE_MS ? value : null;
    } catch (error) {
      return null;
    }
  }

  function write(value) {
    var store = storage();
    if (!store) return;
    try { store.setItem(STORAGE_KEY, JSON.stringify(value)); } catch (error) {}
  }

  function cleanValue(value) {
    if (!value) return '';
    return String(value).replace(/[\u0000-\u001f\u007f]/g, '').slice(0, 256);
  }

  function currentUrl() {
    var url = new URL(window.location.href);
    // Never carry OAuth/MitID callback material through an analytics redirect.
    ['code', 'state', 'error', 'error_description'].forEach(function (key) {
      url.searchParams.delete(key);
    });
    return url.pathname + url.search + url.hash;
  }

  function capture() {
    var params = new URLSearchParams(window.location.search);
    var hasTracking = TRACKING_KEYS.some(function (key) { return params.has(key); });
    var explicitCampaign = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
      .some(function (key) { return params.has(key); });
    var source = params.get('utm_source');
    var medium = params.get('utm_medium');
    var inferred = classifyReferrer(document.referrer);

    var existing = read();
    if (existing) {
      // Keep first-touch attribution, but always return to the latest page the
      // customer visited before starting the MitID flow (usually the cart).
      existing.returnUrl = currentUrl();
      existing.savedAt = Date.now();
      write(existing);
      return;
    }

    if (!source && inferred) source = inferred.source;
    if (!medium && inferred) medium = inferred.medium;
    if (!hasTracking && !inferred) return;

    var tracking = {};
    TRACKING_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) tracking[key] = value;
    });
    if (source && !tracking.utm_source) tracking.utm_source = source;
    if (medium && !tracking.utm_medium) tracking.utm_medium = medium;

    var safeTracking = {};
    Object.keys(tracking).forEach(function (key) {
      var safe = cleanValue(tracking[key]);
      if (safe) safeTracking[key] = safe;
    });
    if (!trackingAllowed(safeTracking, explicitCampaign)) return;

    write({
      savedAt: Date.now(),
      returnUrl: currentUrl(),
      tracking: safeTracking,
      marketing: isMarketingTracking(safeTracking, explicitCampaign)
    });
  }

  function repairCallback() {
    var value = read();
    if (!value || !value.returnUrl) return;

    var target;
    try {
      target = new URL(value.returnUrl, window.location.origin);
    } catch (error) {
      return;
    }
    if (target.origin !== window.location.origin || target.pathname === CALLBACK_PATH) return;

    if (!new URLSearchParams(window.location.search).has('mitid_error') && trackingAllowed(value.tracking || {}, value.marketing)) {
      Object.keys(value.tracking || {}).forEach(function (key) {
        if (!target.searchParams.has(key)) target.searchParams.set(key, cleanValue(value.tracking[key]));
      });
    }

    var store = storage();
    if (store) {
      try { store.removeItem(STORAGE_KEY); } catch (error) {}
    }
    window.location.replace(target.toString());
  }

  function run() {
    if (window.location.pathname === CALLBACK_PATH) {
      // Let the MitID app finish setting its verification state first.
      window.setTimeout(repairCallback, 200);
    } else {
      capture();
    }
  }

  // Cookiebot/Shopify can publish consent after DOMContentLoaded. Re-run
  // capture then so an organic visitor is not lost merely because the banner
  // resolved a moment later.
  document.addEventListener('visitorConsentCollected', run);
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', run);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
